/**
 * Jazyky Bloomu.
 *
 * Bloom je česká aplikace, ale ne uzavřená. Bloom World je z podstaty
 * celosvětový: členka z Česka si hledá organizaci v Berlíně, člověk odjinud
 * si čte, co platí u nás. A do Česka se lidé taky stěhují — trans člověk,
 * který tu žije a neumí česky, je přesně ten, kdo Bloom potřebuje nejvíc.
 *
 * Přeložené je celé rozhraní. Obsah, který napsali lidé – příspěvky, příběhy,
 * popisy odborníků – zůstává v jazyce, ve kterém vznikl. Administrace je
 * česky: je pro správkyni a moderátorky.
 *
 * Texty jsou po jazycích v `src/lang/`. Čeština je zdroj pravdy; nové texty
 * se píšou nejdřív do `lang/cs.js` a odtud se doplňují dál.
 *
 * Proč bez knihovny: potřebujeme slovník, přepínač a správné množné číslo.
 * `Intl.PluralRules` umí prohlížeč sám, zbytek je pár desítek řádků — a
 * nestojí to za další balík v aplikaci, která má být co nejmenší.
 */
import cs from '../lang/cs';
import en from '../lang/en';
import es from '../lang/es';
import fr from '../lang/fr';
import pt from '../lang/pt';
import de from '../lang/de';
import pl from '../lang/pl';
import uk from '../lang/uk';
import vi from '../lang/vi';
import th from '../lang/th';
import id from '../lang/id';
import tl from '../lang/tl';
import zh from '../lang/zh';
import ja from '../lang/ja';

export { mnozne, tvary } from './mnozne';

const KLIC = 'bloom_jazyk';

/**
 * Nabízené jazyky.
 *
 * `overeno` říká, jestli překlad viděl někdo, kdo tím jazykem mluví. U trans
 * témat na tom záleží víc než jinde: rodová shoda, oslovení a názvosloví
 * tranzice se v každém jazyce řeší jinak a strojový překlad tu umí urazit.
 * Neověřené jazyky to přiznávají v patičce, ať to čtenář ví a může to opravit.
 */
export const JAZYKY = [
  { kod: 'cs', nazev: 'Čeština', zkratka: 'CS', overeno: true },
  { kod: 'en', nazev: 'English', zkratka: 'EN', overeno: true },
  { kod: 'es', nazev: 'Español', zkratka: 'ES', overeno: false },
  { kod: 'fr', nazev: 'Français', zkratka: 'FR', overeno: false },
  { kod: 'pt', nazev: 'Português', zkratka: 'PT', overeno: false },
  { kod: 'de', nazev: 'Deutsch', zkratka: 'DE', overeno: false },
  { kod: 'pl', nazev: 'Polski', zkratka: 'PL', overeno: false },
  { kod: 'uk', nazev: 'Українська', zkratka: 'UK', overeno: false },
  { kod: 'vi', nazev: 'Tiếng Việt', zkratka: 'VI', overeno: false },
  { kod: 'th', nazev: 'ไทย', zkratka: 'TH', overeno: false },
  { kod: 'id', nazev: 'Bahasa Indonesia', zkratka: 'ID', overeno: false },
  { kod: 'tl', nazev: 'Filipino', zkratka: 'TL', overeno: false },
  { kod: 'zh', nazev: '简体中文', zkratka: 'ZH', overeno: false },
  { kod: 'ja', nazev: '日本語', zkratka: 'JA', overeno: false },
];

const SLOVNIKY = { cs, en, es, fr, pt, de, pl, uk, vi, th, id, tl, zh, ja };

/** Jestli je jazyk mezi nabízenými. */
export const znamyJazyk = (kod) => JAZYKY.some((j) => j.kod === kod);

/** Údaje o jazyce (název, zkratka, ověřenost). */
export const dejJazyk = (kod) => JAZYKY.find((j) => j.kod === kod) || JAZYKY[0];

/**
 * Jazyky, které se mají chovat jako jiný náš jazyk.
 *
 * Slovenština dostane češtinu: rozumí si a slovenský překlad zatím není.
 * Ostatní slovanské jazyky češtinu nedostanou – rusky mluvící člověk jí
 * nerozumí a angličtina mu pomůže víc.
 *
 * `fil` je tu proto, že filipínština má dvě značky: prohlížeče posílají
 * `fil`, ale kód souboru je `tl`. Bez toho by z `fil` po oříznutí na dvě
 * písmena vyšlo `fi` – finština, kterou nemáme.
 */
const NAHRADY = { sk: 'cs', fil: 'tl' };

/**
 * Jazyk, kterým se začíná.
 *
 * Nejdřív uložená volba – kdo si jednou přepnul, tomu se to nemá měnit pod
 * rukama. Když žádná není, rozhodne prohlížeč: člověk v Brazílii má dostat
 * portugalštinu rovnou, ne po tápání v přepínači.
 *
 * Čte se celý seznam `navigator.languages`, ne jen první položka: kdo má
 * nastavené třeba „uk, ru, en", má dostat ukrajinštinu, i kdyby ta první
 * byla jazykem, který nemáme. Regionální varianty (`pt-BR`, `zh-TW`) se
 * ořezávají na dvě písmena – rozlišovat je začneme, až budeme mít víc než
 * jednu podobu daného jazyka.
 */
export function vychoziJazyk() {
  try {
    const ulozeny = window.localStorage.getItem(KLIC);
    if (ulozeny && znamyJazyk(ulozeny)) return ulozeny;
  } catch { /* soukromé okno nebo zakázané úložiště – nevadí */ }

  const zProhlizece = typeof navigator === 'undefined'
    ? []
    : (navigator.languages && navigator.languages.length
        ? navigator.languages
        : [navigator.language]);

  for (const znacka of zProhlizece) {
    // `pt-BR` → `pt`, `zh-Hans-CN` → `zh`, `fil-PH` → `fil`.
    const zaklad = String(znacka || '').split('-')[0].toLowerCase();
    const cil = NAHRADY[zaklad] || zaklad.slice(0, 2);
    if (znamyJazyk(cil)) return cil;
  }

  // Neznámý jazyk dostane angličtinu, ne češtinu: srozumitelná je pro víc lidí.
  return 'en';
}

/**
 * Zvolený jazyk pro místa mimo React – hlavně pro axios, který má chodit
 * pro odpovědi ve správném jazyce, a pro hlášky ve `lib/api.js`.
 */
export function aktualniJazyk() {
  return vychoziJazyk();
}

export function ulozJazyk(kod) {
  try {
    window.localStorage.setItem(KLIC, kod);
  } catch { /* neuloží se, po zavření okna se určí znovu podle prohlížeče */ }
}

/**
 * České znění klíče. Slouží k porovnání s názvem, který přišel ze serveru:
 * když se shodují, nikdo nic nepřejmenoval a může se přeložit.
 */
export function cesky(klic) {
  const hodnota = cs[klic];
  return typeof hodnota === 'string' ? hodnota : null;
}

/**
 * Překlad.
 *
 * Když text ve zvoleném jazyce chybí, zkusí se angličtina a pak čeština —
 * rozumět půlce věty je pořád lepší než dívat se na `pod.nabidka`. Klíč se
 * vrátí jen tehdy, když ho nikdo nemá; to je chyba a má být vidět.
 */
export function prelozit(jazyk, klic, parametry) {
  const hodnota = SLOVNIKY[jazyk]?.[klic] ?? en[klic] ?? cs[klic];
  if (hodnota === undefined) return klic;
  return typeof hodnota === 'function' ? hodnota(parametry || {}) : hodnota;
}

/**
 * Text ze serveru, který má českou a anglickou podobu.
 *
 * Jiné jazyky ze serveru zatím nechodí, takže Španěl uvidí angličtinu —
 * u názvu země a poznámky u údaje je to srozumitelnější než čeština.
 */
export const zJazyka = (jazyk, ceskyText, anglicky) =>
  (jazyk === 'cs' ? (ceskyText || anglicky) : (anglicky || ceskyText)) || '';

export { SLOVNIKY };
